import { getLocalStorageCustomer, getLocalStorageItem } from "../../../helperFunctions";
import io from "socket.io-client";
import LiveChatHandler from "./LiveChatHandler";

class ChatbotHandler {
  constructor(conversationDispatch, initialMessage, stateFunctions) {
    this.socket = io(process.env.CHATBOT_URL);
    this.conversationDispatch = conversationDispatch;

    this.stateFunctions = stateFunctions;

    this.restartConversationIntentId = null;
    this.disconnectLiveChatIntentId = null;
    this.bdCallDisconnectedIntentId = null;
    this.gaveConsent = getLocalStorageItem("gaveConsent");

    this.initListeners();
    this.joinRoom({ initialMessage, initialConnection: true });
    this.connectedToZendesk = false;
    this.isTypingTimeout = null;
    this.liveChatHandler = null;
    this.messagesQueue = [];
  }

  emit(event, data) {
    this.socket.emit(event, data);
  }

  initListeners() {
    this.socket.on("roomJoined", (data, pastMessages) =>
      this.handleRoomJoined(data, pastMessages)
    );
    this.socket.on("chatMessage", message => this.handleChatMessage(message));
    this.socket.on("customerData", customer => this.handleCustomerData(customer));
    this.socket.on("connectToLiveChat", data => this.connectToLiveChat(data));
    this.socket.on("disconnect", reason => this.handleDisconnect(reason));
    this.socket.on("enableInput", () => this.stateFunctions.setInputDisabled(false));
    this.socket.on("disableInput", () => this.stateFunctions.setInputDisabled(true));
  }

  handleCustomerData(customerData) {}

  handleDisconnect(reason) {
    if (process.env.GATSBY_ACTIVE_ENV !== "production") {
    console.log(`disconnected because ${reason}, reconnecting...`);}
    this.joinRoom();
  }

  joinRoom(options = {}) {
    let { initialMessage } = options;
    const previousCustomer = getLocalStorageCustomer();

    if (initialMessage) {
      if (typeof initialMessage === "string") {
        initialMessage = {
          type: "text",
          sender: "customer",
          text: initialMessage
        };
      }

      this.conversationDispatch({
        type: "appendMessage",
        message: initialMessage
      });
    }

    let data = {
      customer: previousCustomer,
      options: {
        ...options,
        gaveConsent: this.gaveConsent
      }
    };

    this.emit("join", data);
  }

  /**
   * @param customer
   * @param customer.roomId
   * @param restartConversationIntentId
   * @param disconnectLiveChatIntentId
   * @param bdCallDisconnectedIntentId
   * @param pastMessages
   */
  handleRoomJoined(
    {
      customer,
      restartConversationIntentId,
      disconnectLiveChatIntentId,
      bdCallDisconnectedIntentId
    },
    pastMessages
  ) {
    localStorage.setItem("customer", JSON.stringify(customer));
    this.customer = customer;
    this.roomId = customer.roomId;

    this.restartConversationIntentId = restartConversationIntentId;
    this.disconnectLiveChatIntentId = disconnectLiveChatIntentId;
    this.bdCallDisconnectedIntentId = bdCallDisconnectedIntentId;

    if (pastMessages && pastMessages.messages.length) {
      this.conversationDispatch({
        type: "appendPastMessages",
        messages: pastMessages.messages
      });
    }

    window.onbeforeunload = () => {
      if (process.env.GATSBY_ACTIVE_ENV !== "production") {
      console.log("clearing session");}
      this.leaveRoom();
    };
  }

  leaveRoom() {
    if (process.env.GATSBY_ACTIVE_ENV !== "production") {
    console.log("clearing chatbot session");}

    this.conversationDispatch({ type: "clearConversation" });

    this.emit("clear memory", this.customer.roomId);
    if (this.liveChatHandler) {
      this.liveChatHandler.leaveRoom();
    }
  }

  handleChatMessage(message) {
    /**
     *
     * @type Array
     */
    let responses = message.responses.map(response => ({
      sender: "bot",
      type: "text",
      text: response
    }));

    if (message.options && message.options.length) {
      responses.push({
        sender: "bot",
        type: "options",
        options: message.options
      });
    }

    if (this.messagesQueue.length) {
      this.messagesQueue.push(...responses);
    } else {
      this.messagesQueue.push(...responses);
      this.appendQueuedMessages();
    }
  }

  appendQueuedMessages() {
    if (this.messagesQueue.length) {
      if (this.messagesQueue[0].type === "options") {
        this.conversationDispatch({
          type: "appendMessage",
          message: this.messagesQueue.shift()
        });
        return;
      }

      this.conversationDispatch({ type: "appendTyping" });
      setTimeout(() => {
        this.conversationDispatch({
          type: "appendMessage",
          message: this.messagesQueue.shift()
        });
        this.appendQueuedMessages();
      }, 500);
    }
  }

  connectToLiveChat({ messages }) {
    this.liveChatHandler = new LiveChatHandler(this, messages);
  }

  disconnectLiveChat() {
    delete this.liveChatHandler;
    this.sendPostback(this.disconnectLiveChatIntentId);
  }

  businessDevelopmentCallDisconnected() {
    this.sendPostback(this.bdCallDisconnectedIntentId);
  }

  sendTyping() {
    if (this.liveChatHandler) {
      this.liveChatHandler.sendTyping();
    }
  }

  sendPostback(clickAction, text = "", type = "intent") {
    this.stateFunctions.setInputDisabled(false);
    this.sendMessage({
      type: "postback",
      option: { type, clickAction },
      text
    });
  }

  sendMessage(message) {
    // If message is a string, convert it to an object
    if (typeof message === "string") {
      message = {
        text: message
      };
    }

    // Adding sender attribute
    message.sender = "customer";

    if (!message.type) {
      message.type = "text";
    }

    this.conversationDispatch({ type: "appendMessage", message });

    /**
     * For Live Chat send to zendesk, otherwise send to chatbot
     */
    if (this.liveChatHandler) {
      this.liveChatHandler.sendMessage(message.text);
    } else {
      this.socket.emit("chatMessage", message);
    }
  }
}

export default ChatbotHandler;
